import { useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface IconTileProps {
  icon: LucideIcon;
  name: string;
}

export function IconTile({ icon: Icon, name }: IconTileProps) {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(`<${name} />`);
    setCopied(true);
    toast.success(`Copied: <${name} />`);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="group relative flex flex-col items-center justify-center p-4 border rounded-md bg-background hover:border-primary/50 transition-colors h-24 cursor-help">
            <Icon className="h-6 w-6 text-foreground mb-2" />
            <span className="text-[10px] font-mono text-muted-foreground truncate w-full text-center">
              {name}
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                copyToClipboard();
              }}
              className="absolute top-1 right-1 p-1 opacity-0 group-hover:opacity-100 transition-opacity bg-muted rounded hover:bg-accent"
            >
              {copied ? (
                <Check className="h-3 w-3 text-green-600" />
              ) : (
                <Copy className="h-3 w-3 text-muted-foreground" />
              )}
            </button>
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="bg-slate-900 text-slate-100 border-slate-800">
          <div className="space-y-1">
            <p className="text-[10px] font-bold uppercase text-slate-400">Component</p>
            <p className="font-mono text-xs text-blue-400">{'<'}{name}{' />'}</p>
            <p className="text-[10px] font-bold uppercase text-slate-400 mt-2">Import</p>
            <p className="font-mono text-[10px]">from 'lucide-react'</p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
